import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BillingCycle,
  BillingPortalFlow,
  Permission,
  SubscriptionStatus,
  WorkspacesApi,
  WorkspacePlan,
} from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription, Button, cn } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { CreditCard } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { Can } from '@/core/components/authz/can';
import { BillingCurrentPlanCard } from '@/core/components/billing/billing-current-plan-card';
import { BillingGrantStatusCard } from '@/core/components/billing/billing-grant-status-card';
import {
  BillingInvoicesCard,
  BillingPaymentMethodCard,
} from '@/core/components/billing/billing-payment-invoices-cards';
import { BillingUsageCard } from '@/core/components/billing/billing-usage-card';
import { EnterpriseContactDialog } from '@/core/components/billing/enterprise-contact-dialog';
import { PendingPlanChangeBanner } from '@/core/components/billing/pending-plan-change-banner';
import { resolveSidebarUpgradeTeaserVariant } from '@/core/components/billing/resolve-sidebar-upgrade-teaser-variant';
import { PageHeader } from '@/core/components/page-header';
import { useOpenPortal } from '@/core/hooks/use-billing';
import { useBillingReturn } from '@/core/hooks/use-billing-return';
import {
  useEntitlements,
  useInvalidateWorkspaceEntitlements,
} from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { canPurchaseBillingInApp } from '@/core/billing/can-purchase-billing-in-app';

function WorkspaceBillingBreadcrumb({
  domainLabel,
  billingLabel,
}: {
  domainLabel: string;
  billingLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {billingLabel}
    </>
  );
}

export function WorkspaceBillingPage() {
  const dict = useDictionary();
  const workspaceDict = dict.workspace.billing;
  const overview = workspaceDict.overview;
  const currentWorkspace = useCurrentWorkspace();
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const queryClient = useQueryClient();
  const invalidateEntitlements = useInvalidateWorkspaceEntitlements();
  const {
    plan,
    status,
    seats,
    usage,
    scheduledChange,
    billingGrant,
    hasActiveBillingGrant,
    isLoading,
  } = useEntitlements();
  const { can } = usePermissions();
  const canManage = can(Permission.BILLING_MANAGE);
  const openPortal = useOpenPortal();
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [paygConfirmOpen, setPaygConfirmOpen] = useState(false);

  const feedbackUsage = usage?.feedbackInviteRecipients;
  const feedbackPaygEnabled =
    feedbackUsage?.paygEnabled === true ||
    currentWorkspace?.feedbackPaygEnabled === true;

  const updateFeedbackPayg = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!currentWorkspace?.id) throw new Error('Missing workspace');
      return WorkspacesApi.update({
        path: { id: currentWorkspace.id },
        body: { feedbackPaygEnabled: enabled },
      });
    },
    onSuccess: (_data, enabled) => {
      void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      invalidateEntitlements(currentWorkspace?.id);
      toast.success(
        enabled
          ? workspaceDict.usage.feedbackPaygEnabledToast
          : workspaceDict.usage.feedbackPaygDisabledToast,
      );
      setPaygConfirmOpen(false);
    },
    onError: () => {
      toast.error(workspaceDict.usage.feedbackPaygError);
    },
  });

  useBillingReturn();

  const breadcrumb = (
    <WorkspaceBillingBreadcrumb
      domainLabel={dict.nav.navDomainWorkspaceAdmin}
      billingLabel={dict.nav.billing}
    />
  );

  const isEnterprise = plan === WorkspacePlan.ENTERPRISE;
  const hasPaidSubscription =
    plan !== WorkspacePlan.FREE &&
    (status === SubscriptionStatus.ACTIVE ||
      status === SubscriptionStatus.TRIALING ||
      status === SubscriptionStatus.PAST_DUE);
  const hasStripeCustomer = Boolean(currentWorkspace?.stripeCustomerId);
  const canOpenPortal = hasPaidSubscription || hasStripeCustomer;
  const promoteCheckoutTrial =
    plan != null &&
    resolveSidebarUpgradeTeaserVariant({ plan, status }) === 'trialStart';

  const cycleLabel =
    currentWorkspace?.billingCycle === BillingCycle.YEARLY
      ? workspaceDict.cycleYearly
      : currentWorkspace?.billingCycle === BillingCycle.MONTHLY
        ? workspaceDict.cycleMonthly
        : undefined;

  const paidActions =
    canPurchaseBillingInApp() && canManage && !isEnterprise ? (
      <Can permission={Permission.BILLING_MANAGE}>
        <div className={cn('flex gap-2', 'flex-col')}>
          <Button
            variant="primary"
            className="w-full"
            onClick={() =>
              navigateShell(shellPaths.moreWorkspaceBillingUpgrade)
            }
            disabled={openPortal.isPending}
          >
            {overview.changePlanCta}
          </Button>
          {hasPaidSubscription ? (
            <Button
              variant="outline"
              className="w-full"
              disabled={openPortal.isPending}
              onClick={() => openPortal.mutate(undefined)}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {workspaceDict.manageBilling}
            </Button>
          ) : null}
        </div>
      </Can>
    ) : canPurchaseBillingInApp() && canManage && isEnterprise ? (
      <Can permission={Permission.BILLING_MANAGE}>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setEnterpriseOpen(true)}
        >
          {workspaceDict.contactSales}
        </Button>
      </Can>
    ) : null;

  const mainColumn = (
    <>
      {hasActiveBillingGrant && billingGrant ? (
        <BillingGrantStatusCard billingGrant={billingGrant} />
      ) : (
        <BillingCurrentPlanCard
          plan={plan}
          status={status}
          workspace={currentWorkspace}
          seats={seats ?? 1}
          isLoading={isLoading}
          isMobile={isMobile}
          promoteCheckoutTrial={promoteCheckoutTrial}
          canManage={canManage}
          paidActions={!isFreePlan(plan) ? paidActions : undefined}
          cycleLabel={cycleLabel}
        />
      )}
      <BillingUsageCard
        plan={plan}
        seats={seats}
        usage={usage}
        feedbackPaygEnabled={feedbackPaygEnabled}
        feedbackPaygPending={updateFeedbackPayg.isPending}
        canManage={canManage}
        onFeedbackPaygChange={(checked) => {
          if (checked && !feedbackPaygEnabled) {
            setPaygConfirmOpen(true);
            return;
          }
          if (!checked && feedbackPaygEnabled) {
            updateFeedbackPayg.mutate(false);
          }
        }}
      />
    </>
  );

  const sideColumn = (
    <>
      <BillingPaymentMethodCard
        hasPaymentMethodHint={hasPaidSubscription}
        canManage={canManage && canPurchaseBillingInApp()}
        isPending={openPortal.isPending}
        onManage={() =>
          openPortal.mutate({ flow: BillingPortalFlow.PAYMENT_METHOD_UPDATE })
        }
      />
      <BillingInvoicesCard
        hasPaidSubscription={hasPaidSubscription}
        canManage={canManage && canPurchaseBillingInApp()}
        canOpenPortal={canOpenPortal && canPurchaseBillingInApp()}
        isPending={openPortal.isPending}
        onOpenPortal={() => openPortal.mutate(undefined)}
      />
    </>
  );

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            breadcrumb={breadcrumb}
            title={isMobile ? overview.titleMobile : workspaceDict.title}
            description={
              !isMobile && currentWorkspace?.name
                ? workspaceDict.subtitle.replace(
                    '{name}',
                    currentWorkspace.name,
                  )
                : undefined
            }
          />

          <div className="flex w-full min-w-0 flex-col gap-5 pb-6">
            <PendingPlanChangeBanner scheduledChange={scheduledChange} />

            {status === SubscriptionStatus.PAST_DUE ? (
              <Alert variant="destructive">
                <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span>{workspaceDict.pastDueBanner}</span>
                  {canPurchaseBillingInApp() ? (
                    <Can permission={Permission.BILLING_MANAGE}>
                      <Button
                        size="sm"
                        variant="outline"
                        className={cn(isMobile && 'w-full')}
                        disabled={openPortal.isPending}
                        onClick={() =>
                          openPortal.mutate({
                            flow: BillingPortalFlow.PAYMENT_METHOD_UPDATE,
                          })
                        }
                      >
                        {workspaceDict.updatePayment}
                      </Button>
                    </Can>
                  ) : null}
                </AlertDescription>
              </Alert>
            ) : null}

            {canManage && !canPurchaseBillingInApp() ? (
              <Alert>
                <AlertDescription>
                  {workspaceDict.nativeWebOnlyNotice}
                </AlertDescription>
              </Alert>
            ) : null}

            {!canManage ? (
              <Alert>
                <AlertDescription>
                  {workspaceDict.readOnlyHint}
                </AlertDescription>
              </Alert>
            ) : null}

            {isMobile ? (
              <div className="flex flex-col gap-4">
                {mainColumn}
                {sideColumn}
              </div>
            ) : (
              <div className="grid grid-cols-[minmax(0,1fr)_360px] items-start gap-5">
                <div className="flex flex-col gap-5">{mainColumn}</div>
                <div className="flex flex-col gap-5">{sideColumn}</div>
              </div>
            )}
          </div>

          <ResponsiveDialog
            open={paygConfirmOpen}
            onOpenChange={setPaygConfirmOpen}
          >
            <ResponsiveDialogContent className="sm:max-w-md">
              <ResponsiveDialogHeader>
                <ResponsiveDialogTitle>
                  {workspaceDict.usage.feedbackPaygConfirmTitle}
                </ResponsiveDialogTitle>
                <ResponsiveDialogDescription>
                  {workspaceDict.usage.feedbackPaygConfirmBody}
                </ResponsiveDialogDescription>
              </ResponsiveDialogHeader>
              <ResponsiveDialogFooter
                className={cn(
                  isMobile
                    ? 'gap-2 border-0 bg-background pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
                    : undefined,
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  className={cn(isMobile && 'w-full')}
                  onClick={() => setPaygConfirmOpen(false)}
                >
                  {workspaceDict.usage.feedbackPaygCancel}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className={cn(isMobile && 'w-full')}
                  disabled={updateFeedbackPayg.isPending}
                  onClick={() => updateFeedbackPayg.mutate(true)}
                >
                  {workspaceDict.usage.feedbackPaygConfirm}
                </Button>
              </ResponsiveDialogFooter>
            </ResponsiveDialogContent>
          </ResponsiveDialog>

          <EnterpriseContactDialog
            open={enterpriseOpen}
            onOpenChange={setEnterpriseOpen}
          />
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}

function isFreePlan(plan: WorkspacePlan | undefined): boolean {
  return plan === WorkspacePlan.FREE || plan == null;
}
